import React, { forwardRef, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import scaleButton from '../../assets/scale-button.svg'
import { useWindowSize } from 'usehooks-ts'

type TVGuideProps = {
  data: TVGuideEntry[]
  partei: string
  color: string
  colorMode: 'dark' | 'light'
  headerheight: number
  menuHeight: number
  partyInViewUpdate: (party: string) => void // Type the onPartyUpdate as a function
}

// Define a type for individual TV Guide entries
type TVGuideEntry = {
  partei: string
  bildname: string
  sendezeit: string
  sendung: string
  beschreibung: string
  index: number
}
const partyLogos = require.context('../../assets/logos', true)
const TVGuide = forwardRef(
  (
    {
      data,
      partei,
      color,
      colorMode,
      headerheight,
      menuHeight,
      partyInViewUpdate,
    }: TVGuideProps,
    ref: any
  ) => {
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const [selectedImage, setSelectedImage] = useState('')

    const [selectedBroadcast, setSelectedBroadcast] = useState<string | null>(
      null
    )
    const [selectedParty, setSelectedParty] = useState<string | null>(null)
    const [selectedTime, setSelectedTime] = useState<string | null>(null)
    const [selectedDescription, setSelectedDescription] = useState<
      string | null
    >(null)
    const [currentImageIndex, setCurrentImageIndex] = useState(0)

    const programmImageItemRef = useRef<HTMLImageElement>(null)
    const [programmImageItemHeight, setProgrammImageItemHeight] = useState(0)

    // console.log('ImageHeight:', programmImageItemHeight)
    // Get current date and format it
    // const currentDate = new window.Date()
    // const options: Intl.DateTimeFormatOptions = {
    //   weekday: 'long',
    //   year: 'numeric',
    //   month: 'long',
    //   day: 'numeric',
    // }
    // const formattedDate = currentDate.toLocaleDateString(undefined, options)
    const PartyOrder = ['Grüne', 'SP', 'GLP', 'DieMitte', 'FDP', 'SVP']

    let orderedData = Object.entries(groupByParty(data))
      // Sort by PartyOrder
      .sort(([partyA], [partyB]) => {
        if (PartyOrder && partyA && partyB) {
          return PartyOrder.indexOf(partyA) - PartyOrder.indexOf(partyB)
        } else {
          console.log('Debug:', PartyOrder, partyA, partyB)
          // return a default value or handle error
          return 0
        }
      })
      // Flatten array and sort by 'sendezeit' within each party
      .flatMap(([party, partyItems]) =>
        partyItems.sort((a, b) => a.sendezeit.localeCompare(b.sendezeit))
      )

      // Add the absoluteIndex property
      .map((item, i) => ({ ...item, absoluteIndex: i }))

    // console.log(currentImageIndex)

    const goToPrevImage = () => {
      let prevImageIndex = currentImageIndex
      let prevImage

      // Cycle through orderedData to find image back from the current position.
      do {
        prevImageIndex =
          prevImageIndex - 1 < 0 ? orderedData.length - 1 : prevImageIndex - 1
        prevImage = orderedData[prevImageIndex]
      } while (!prevImage.bildname)

      setSelectedImage(
        'https://interaktiv.tagesanzeiger.ch/2024/ParteiTVProgramm/' +
          prevImage.bildname
      )
      setSelectedBroadcast(prevImage.sendung)
      setSelectedParty(prevImage.partei)
      setSelectedTime(prevImage.sendezeit)
      setSelectedDescription(prevImage.beschreibung)
      setCurrentImageIndex(prevImageIndex)
    }

    const goToNextImage = () => {
      let nextImageIndex = currentImageIndex
      let nextImage

      // Cycle through orderedData to find image ahead from the current position.
      do {
        nextImageIndex = (nextImageIndex + 1) % orderedData.length
        nextImage = orderedData[nextImageIndex]
      } while (!nextImage.bildname)

      setSelectedImage(
        'https://interaktiv.tagesanzeiger.ch/2024/ParteiTVProgramm/' +
          nextImage.bildname
      )
      setSelectedBroadcast(nextImage.sendung)
      setSelectedParty(nextImage.partei)
      setSelectedTime(nextImage.sendezeit)
      setSelectedDescription(nextImage.beschreibung)
      setCurrentImageIndex(nextImageIndex)
    }

    useEffect(() => {
      // Disable scrolling if lightbox is open
      if (lightboxOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        // Enable scrolling if lightbox is closed
        document.body.style.overflow = 'unset'
      }
    }, [lightboxOpen])

    useEffect(() => {
      const updateHeight = () => {
        if (programmImageItemRef.current) {
          setProgrammImageItemHeight(programmImageItemRef.current.offsetHeight)
        }
      }

      window.addEventListener('resize', updateHeight)

      // Clean up on unmount
      return () => {
        window.removeEventListener('resize', updateHeight)
      }
    }, [])

    useEffect(() => {
      if (programmImageItemRef.current) {
        setProgrammImageItemHeight(programmImageItemRef.current.offsetHeight)
      }
    }, [programmImageItemRef])

    const windowSize = useWindowSize()
    const isMobile = windowSize.width < 600

    //absoluteIndex = index over all entries
    let absoluteIndex = 0

    function groupByParty(data: TVGuideEntry[]) {
      return data.reduce((acc, item) => {
        const party = item.partei // Use 'partei' as this is the property name in your TVGuideEntry type
        if (!acc[party]) {
          acc[party] = []
        }
        acc[party].push(item)
        return acc
      }, {} as Record<string, TVGuideEntry[]>) // Initialize accumulator as an object with keys as string and values as array of TVGuideEntry
    }
    const dynamicHeaderHeight =
      headerheight + menuHeight + (isMobile ? 200 : 100) // Replace with your actual header height in pixels.
    // console.log('HH:', headerheight)
    // console.log('MH:', menuHeight)
    // console.log('Total:', dynamicHeaderHeight)
    const intersectingParty = useRef<string | null>()
    const previousIntersectingParty = useRef<string | null>()
    useEffect(() => {
      if (ref.current) {
        const parties = Object.keys(groupByParty(data))
        let observers: Record<string, IntersectionObserver> = {}

        parties.forEach((partei: string) => {
          const partyContainer = ref.current.querySelector(`#${partei}`)

          if (
            partyContainer &&
            partyContainer.clientHeight > 10 &&
            !observers[partei]
          ) {
            observers[partei] = new IntersectionObserver(
              ([entry]) => {
                if (
                  entry.isIntersecting &&
                  intersectingParty.current !== partei
                ) {
                  previousIntersectingParty.current = intersectingParty.current
                  intersectingParty.current = partei
                  partyInViewUpdate(partei)
                }
                if (
                  !entry.isIntersecting &&
                  intersectingParty.current === partei
                ) {
                  intersectingParty.current = previousIntersectingParty.current
                  partyInViewUpdate(previousIntersectingParty.current ?? '')
                }
              },
              {
                rootMargin: `-${dynamicHeaderHeight}px 0px 0px 0px`,
                threshold: [0], // <-- Multiple thresholds
              }
            )

            observers[partei].observe(partyContainer)
          }
        })
        return () => {
          parties.forEach((partei: string) => {
            observers[partei]?.disconnect()
          })
        }
      }
    }, [data, ref, dynamicHeaderHeight, partyInViewUpdate])

    return (
      <TVGuideContainer
        className={colorMode === 'dark' ? 'dark' : ''}
        ref={ref}
      >
        {/* {isVisible && (
          <p className={isVisible ? 'infoBox fade-in' : 'infoBox fade-out'}>
            Wählen Sie Ihr Parteifernsehprogramm
          </p>
        )} */}
        <InnerWrapper color={color}>
          {Object.entries(groupByParty(data)) // Group data by parties and map through groups
            .sort(([partyA], [partyB]) => {
              return PartyOrder.indexOf(partyA) - PartyOrder.indexOf(partyB)
            })
            .map(([party, partyItems], partyIndex) => (
              <PartyContainer
                id={party}
                key={partyIndex}
                className='partyContainer'
              >
                <InfoParty>
                  {/* <Rectangle
                    className={party}
                    even={partyIndex % 2 === 0}
                  ></Rectangle> */}
                  <img
                    src={partyLogos(
                      './' +
                        party
                          .toLowerCase()
                          .replace(
                            /ä|ö|ü/gi,
                            (match) =>
                              ({ ä: 'ae', ö: 'oe', ü: 'ue' }[match || ''] || '')
                          ) +
                        (colorMode !== 'dark' ? '-positiv' : '') +
                        '.svg'
                    )}
                    alt={'logo' + party}
                    className={'icon ' + party}
                  />
                  <div className={party}>Programmvorschau</div>
                </InfoParty>

                {/* Each party has its own container */}
                {partyItems
                  .sort((a, b) => a.sendezeit.localeCompare(b.sendezeit)) // Sort items by sendezeit
                  .map((item) => {
                    let currentItemIndex = absoluteIndex++
                    const isFirstItem = currentItemIndex === 0
                    return (
                      <div
                        className={item.bildname ? 'entry' : 'entry noImage'}
                        key={currentItemIndex}
                      >
                        {((isMobile && item.bildname) || !isMobile) && (
                          <div
                            className={
                              item.bildname
                                ? 'programmImage'
                                : 'programmImage noImage'
                            }
                          >
                            {item.bildname && (
                              <>
                                <img
                                  src={
                                    'https://interaktiv.tagesanzeiger.ch/2024/ParteiTVProgramm/' +
                                    item.bildname
                                  }
                                  alt={item.bildname}
                                  className={'programmImageItem'}
                                  ref={
                                    isFirstItem ? programmImageItemRef : null
                                  }
                                  onLoad={() => {
                                    if (
                                      isFirstItem &&
                                      programmImageItemRef.current
                                    ) {
                                      setProgrammImageItemHeight(
                                        programmImageItemRef.current
                                          .offsetHeight
                                      )
                                    }
                                  }}
                                />
                                <img
                                  src={scaleButton}
                                  alt='Scale Button'
                                  className='scaleButton'
                                  style={{
                                    top: `${programmImageItemHeight - 49}px`,
                                  }}
                                  onClick={() => {
                                    setLightboxOpen(true)
                                    setSelectedImage(
                                      'https://interaktiv.tagesanzeiger.ch/2024/ParteiTVProgramm/' +
                                        item.bildname
                                    )
                                    setSelectedBroadcast(item.sendung)
                                    setSelectedParty(item.partei)
                                    setSelectedTime(item.sendezeit)
                                    setSelectedDescription(item.beschreibung)
                                    setCurrentImageIndex(currentItemIndex) // Use current absolute index
                                  }}
                                />
                              </>
                            )}
                          </div>
                        )}
                        {(item.sendezeit || item.sendung || !isMobile) && (
                          <div className='info'>
                            {item.sendezeit && (
                              <span
                                className={
                                  'time ' +
                                  party +
                                  (colorMode === 'dark' && ' dark')
                                }
                              >
                                {item.sendezeit}
                              </span>
                            )}
                            <h4 className='title'>{item.sendung}</h4>
                            <div className='description'>
                              {item.beschreibung}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
              </PartyContainer>
            ))}
        </InnerWrapper>

        {lightboxOpen && (
          <LightBox
            // onClick={() => setLightboxOpen(false)}
            headerheight={headerheight}
          >
            <LightBoxContent>
              <ImageWrapper>
                <LightboxImage src={selectedImage} alt='Selected' />
                <figcaption style={{ color: '#fff', marginTop: '15px' }}>
                  <h4 className='title'>{selectedBroadcast}</h4>
                  <div className='description'>{selectedDescription}</div>

                  <div className='description logo'>
                    {' '}
                    <img
                      src={partyLogos(
                        './' +
                          (selectedParty?.toLowerCase() || '').replace(
                            /ä|ö|ü/gi,
                            (match) =>
                              ({ ä: 'ae', ö: 'oe', ü: 'ue' }[match || ''] || '')
                          ) +
                          '.svg'
                      )}
                      alt={'logo' + selectedParty}
                      className='icon'
                    />
                    · {selectedTime}&nbsp;Uhr
                  </div>
                </figcaption>

                <NavigationWrapper>
                  <PreviousNextButton
                    onClick={(e) => {
                      e.stopPropagation()
                      goToPrevImage()
                    }}
                  >
                    &#60;
                  </PreviousNextButton>

                  <PreviousNextButton
                    onClick={(e) => {
                      e.stopPropagation()
                      goToNextImage()
                    }}
                  >
                    &#62;
                  </PreviousNextButton>
                </NavigationWrapper>
              </ImageWrapper>

              <CloseButton
                onClick={(e) => {
                  e.stopPropagation() // Prevent the event from bubbling up and closing the lightbox
                  setLightboxOpen(false)
                }}
              >
                &times;
              </CloseButton>
            </LightBoxContent>
          </LightBox>
        )}
      </TVGuideContainer>
    )
  }
)

const PartyContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 1.5em 1em 3em 1em;
  /* @media screen and (max-width: 711px) {
    padding: 90px 1em;
  } */
  &#Grüne {
    background-color: rgba(132, 181, 71, 0.25);
    .time {
      background-color: rgba(132, 181, 71, 1);
      &.dark {
        background-color: rgba(132, 181, 71, 0.6);
      }
    }
  }
  &#SP {
    background-color: rgba(240, 85, 77, 0.25);
    .time {
      background-color: rgba(240, 85, 77, 1);
      &.dark {
        background-color: rgba(240, 85, 77, 0.6);
      }
    }
  }
  &#GLP {
    background-color: rgba(196, 196, 61, 0.25);
    .time {
      background-color: rgba(196, 196, 61, 1);
      &.dark {
        background-color: rgba(196, 196, 61, 0.6);
      }
    }
  }
  &#DieMitte {
    background-color: rgba(214, 134, 43, 0.25);
    .time {
      background-color: rgba(214, 134, 43, 1);
      &.dark {
        background-color: rgba(214, 134, 43, 0.6);
      }
    }
  }
  &#FDP {
    background-color: rgba(56, 114, 181, 0.25);
    .time {
      background-color: rgba(56, 114, 181, 1);
      &.dark {
        background-color: rgba(56, 114, 181, 0.6);
      }
    }
  }
  &#SVP {
    background-color: rgba(75, 138, 62, 0.25);
    .time {
      background-color: rgba(75, 138, 62, 1);
      &.dark {
        background-color: rgba(75, 138, 62, 0.6);
      }
    }
  }
`
const InfoParty = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
  margin-bottom: 0.5em;
  @media screen and (max-width: 599px) {
    margin-bottom: 0.75em;
    flex-direction: column;
  }

  img {
    height: 120px;
    width: 120px;
    @media screen and (max-width: 599px) {
      height: 115px;
      width: 115px;
    }
    @media screen and (min-width: 600px) {
    &.Grüne, &.GLP, &.FDP, &.SVP, &.DieMitte{ 
      margin-left: -0.6em;
    }
    &.SP {
      margin-left: -1.3em;
    }
  }
  }

  div {
    font-weight: 700;
    font-size: 2rem;
    z-index: 1;
    margin-left: 0em;
    @media screen and (max-width: 599px) {
      margin-top: -1em;
    }
    @media screen and (min-width: 600px) {
    &.Grüne {
      margin-left: 0.5em;
    }
    &.SVP {
      margin-left: 0.5em;
    }
  }

  }
  /* div.after::after {
    display: block;
    clear: both;
    width: 100%;
    margin: 2rem auto 2rem;
    border-top: 2px solid #555555;
    content: '';
  } */

  /* img {
    box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
    border-radius: 50%;
    height: 100px;
    width: 100px;
    border: 1px solid #fff;
  } */


`

// const Rectangle = styled.div<{ even?: boolean }>`
//   position: absolute;
//   width: 80%;
//   height: 43%;
//   @media screen and (max-width: 599px) {
//     height: 37%;
//   }
//   top: 50%;
//   transform: translateY(-50%);
//   margin-right: ${(props) => (props.even ? '-1em' : '0')};

//   left: ${(props) => (props.even ? 'auto' : '0')};
//   right: ${(props) => (props.even ? '0' : 'auto')};

//   &.Grüne {
//     background: linear-gradient(to right, #84b547, transparent);
//   }
//   &.SP {
//     right: 0;
//     background: linear-gradient(to left, #f0554d, transparent);
//   }
//   &.GLP {
//     background: linear-gradient(to right, #c4c43d, transparent);
//   }
//   &.DieMitte {
//     background: linear-gradient(to left, #d6862b, transparent);
//   }
//   &.FDP {
//     background: linear-gradient(to right, #3872b5, transparent);
//   }
//   &.SVP {
//     background: linear-gradient(to left, #4b8a3e, transparent);
//   }
// `

const TVGuideContainer = styled.div`
  p.infoBox {
    position: absolute; /*Changed from absolute to relative */
    background-color: #ffffff;
    padding: 1em 0.5em;
    border-radius: 5px;
    font-size: 0.8em;
    color: var(--brandblue-main);
    opacity: 1;
    max-width: 180px;
    line-height: 2.1rem;
    margin-left: 15px;
    text-align: center;

    &.fade-in {
      animation-name: fadeout;
      animation-delay: 4s; /* this is the delay before the animation starts */
      animation-duration: 2s; /* this is how long it will take for the element to completely fade out */
      animation-fill-mode: forwards; /* keeps the final state after animation ends */
    }

    @keyframes fadeout {
      from {
        opacity: 1;
      }
      to {
        opacity: 0;
      }
    }
  }

  p.infoBox::before {
    content: '';
    position: absolute;
    top: -9px; /* Adjust according to triangle size */
    left: 20%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 10px solid transparent; /* Half of triangle's base length */
    border-right: 10px solid transparent; /* Half of triangle's base length */
    border-bottom: 10px solid #ffffff; /* Triangle's height and color */
  }

  /* background-color: none;
  color: var(--gray-1); */

  &.dark {
    background-color: #1b1b1b;
    color: var(--gray-5);
    .entry {
      /* background-color: #ffffff24;
      box-shadow: rgba(26, 26, 26, 0.3) 0px 2px 8px 0px; */
      .info {
        h4.title {
          color: var(--gray-5);
        }
        .description {
          color: var(--gray-4);
        }
      }
    }
  }

  .entry {
    display: flex;
    flex-direction: row;
    gap: 25px;
    width: 100%;
    box-sizing: content-box;
    @media screen and (min-width: 600px) {
      &.noImage {
        padding: 8px 0;
      }
    }
    /* background-color: #ffffff62;
    box-shadow: rgba(99, 99, 99, 0.15) 0px 2px 8px 0px; */

    @media screen and (max-width: 599px) {
      flex-direction: column-reverse;
      gap: 16px;
      margin-bottom: 0.65em;
    }
    .programmImage {
      position: relative;
      width: 100%;
      overflow: hidden;
      flex: 2;
      @media screen and (max-width: 599px) {
        flex: auto;
      }
    }

    .programmImage:not(.noImage)::before {
      content: '';
      display: block;
      padding-top: 66.67%; /* Represents 2/3 for a 3:2 aspect ratio */
    }

    img.scaleButton {
      position: absolute;
      bottom: 15px;
      right: 15px;
      width: 36px;
      height: auto;
      cursor: pointer;
    }

    .programmImage img.programmImageItem {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: auto;
      object-fit: cover;
    }

    .info {
      /* padding: 1em; */
      line-height: 1.4;
      flex: 3;
      .time {
        font-size: 0.9em;
        color: white;
        border-radius: 3px;
        padding: 0.15em 0.4em;
        display: inline-block;
        margin-bottom: 0.3em;
        /* box-shadow: rgba(0, 0, 0, 0.1) 0px 2px 8px 0px; */

        @media screen and (max-width: 1023px) {
          font-size: 0.85em;
        }
        @media screen and (max-width: 711px) {
          font-size: 0.8em;
        }
        @media screen and (max-width: 599px) {
          font-size: 0.85em;
        }
      }
      h4.title {
        margin: 0.25em 0;
        font-size: 0.95em;
        letter-spacing: 0.15px;

        @media screen and (max-width: 1023px) {
          font-size: 0.9em;
        }

        @media screen and (max-width: 711px) {
          font-size: 0.85em;
        }
        @media screen and (max-width: 599px) {
          font-size: 0.9em;
        }
      }
      .description {
        color: var(--gray-2);
        font-size: 0.9em;

        @media screen and (max-width: 1023px) {
          font-size: 0.85em;
        }

        @media screen and (max-width: 711px) {
          font-size: 0.8em;
        }
        @media screen and (max-width: 599px) {
          font-size: 0.85em;
        }
      }
    }
  }
`

const InnerWrapper = styled.div`
  display: grid;
  row-gap: 2px;
  /* background-color: ${(props) =>
    props.color ? props.color : 'rgba(75, 138, 62, 0.25)'}; */
  /* padding: 1em 1em; */
`
const ImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  figcaption {
    h4.title {
      margin: 0.25em 0;
      font-size: 0.95em;
      letter-spacing: 0.15px;
      line-height: 2.1rem;
      @media screen and (max-width: 711px) {
        font-size: 0.85em;
      }
    }
    .description {
      color: var(--gray-3);
      font-size: 0.9em;
      line-height: 2.1rem;
      @media screen and (max-width: 711px) {
        font-size: 0.8em;
      }
      &.logo {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 0.5em;
        /* justify-content: center; */
        img {
          width: 50px;
          min-height: 35px;
        }
      }
    }
  }
`

const LightBox = styled.div<{ headerheight?: number }>`
  position: fixed;
  top: ${({ headerheight }) => headerheight ?? 0}px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.91);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  z-index: 1000;
  padding: 0 1em;
`
const LightBoxContent = styled.div`
  max-width: 48%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 1550px) {
    max-width: 60%;
  }
  @media screen and (max-width: 1024px) {
    max-width: 100%;
    max-height: auto;
  }
`
const LightboxImage = styled.img`
  width: 100%;
  @media screen and (max-width: 768px) {
    // Change 768px to whatever breakpoint you need
    max-width: 100%;
    max-height: auto;
  }
`

const CloseButton = styled.button`
  position: absolute;
  padding: 10px;
  border: none;
  border-radius: 50%;
  background-color: #fff;
  width: 50px;
  height: 50px;
  font-size: 1.2em;
  line-height: 0;
  color: #121212;
  cursor: pointer;
  background-color: var(--yellow-main);
  &:hover,
  &:active {
    background-color: #8e7f34;
  }
  @media screen and (max-width: 711px) {
    bottom: 40px; // changed from 'top' to 'bottom'
    right: 20px;
    transform: translateX(-50%); // added to center align horizontall
  }
  @media screen and (min-width: 712px) {
    top: 18px;
    right: 18px;
  }
`
const NavigationWrapper = styled.div`
  display: flex;
  justify-content: center;
`
const PreviousNextButton = styled.button`
  padding: 1em;
  /* border: none;
  border-radius: 50%; */

  margin-top: 1.75em;
  /* border: 1px solid var(--gray-6); */
  border-radius: 50%;
  border: none;
  background-color: var(--gray-2);
  box-shadow: rgba(0, 0, 0, 0.5) 0px 2px 8px 0px;
  color: var(--gray-6);
  width: 50px;
  height: 50px;
  &:first-of-type {
    margin-right: 1em;
  }
  font-size: 0.9em;
  line-height: 0;
  cursor: pointer;
  &:hover,
  &:active {
    opacity: 0.6;
    /* border: 1px solid var(--yellow-main); */
  }
`

export default TVGuide
